import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";
import { Tabs, Tab } from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import useIsMobile from "hooks/useIsMobile";
import Slide from "@mui/material/Slide";
import useScrollTrigger from "@mui/material/useScrollTrigger";

const PRODUCT_LIST_PATH = "/api/v1/product/listv2";
const UPDATE_API_PATH = "/api/v1/product/update";
const BATCH_NOTE_PRIVATE_API_PATH = "/api/v1/product/batch-note-private";
const UPLOAD_IMAGE_PATH = "/api/v1/product/uploadimage";
const UPDATE_IMAGE_PATH = "/api/v1/product/updateimage";
const outlineButtonSx = {
  borderColor: "#1976d2",
  color: "#1976d2",
  fontWeight: 600,
  "&:hover": {
    borderColor: "#115293",
    backgroundColor: "rgba(25, 118, 210, 0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(25, 118, 210, 0.45)",
    borderColor: "rgba(25, 118, 210, 0.3)",
  },
};
const primaryButtonSx = {
  backgroundColor: "#1976d2",
  color: "#ffffff",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "#115293",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(25, 118, 210, 0.35)",
    color: "rgba(255, 255, 255, 0.85)",
  },
};
const UTF8_BOM = "\uFEFF";
const UTF8_BOM_BYTES = [0xef, 0xbb, 0xbf];
const trimTrailingSlash = (value) => value.replace(/\/+$/, "");
const stripGraphQLSuffix = (value) => value.replace(/\/GraphQLHandler$/, "");
const buildBackendBase = (graphqlEndpoint) =>
  trimTrailingSlash(stripGraphQLSuffix(graphqlEndpoint || ""));
const resolveBackendBase = (graphqlEndpoint) => {
  const base = buildBackendBase(graphqlEndpoint);
  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  if (!base || base === "undefined" || base === "null") return fallback;
  try {
    new URL(base);
    return base;
  } catch (error) {
    return fallback || base;
  }
};
const selectSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
  },
  "& .MuiSelect-select": {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
};

const OPTION_LABELS = {
  // options_product_name: "productsPage.optionLabels.productName",
  options_product_specs: "productsPage.optionLabels.productSpecs",
  options_vender_code: "productsPage.optionLabels.venderCode",
  options_vender_name: "productsPage.optionLabels.venderName",
  options_brand: "productsPage.optionLabels.brand",
  options_movie_or_portrait: "productsPage.optionLabels.movieOrPortrait",
  options_nature: "productsPage.optionLabels.nature",
  options_product_series: "productsPage.optionLabels.productSeries",
  options_category_name: "productsPage.optionLabels.categoryName",
  options_p_image: "productsPage.optionLabels.images",
  options_p_videos: "productsPage.optionLabels.videos",
  options_packaging_size: "productsPage.optionLabels.packagingSize",
  options_capsule_size: "productsPage.optionLabels.capsuleSize",
  options_valid_until: "productsPage.optionLabels.validUntil",
  options_mall_listing: "productsPage.optionLabels.mallListing",
  options_pr_method: "productsPage.optionLabels.prMethod",
  options_w_attributes: "productsPage.optionLabels.wAttributes",
  options_p_brand: "productsPage.optionLabels.pBrand",
  options_p_attributes: "productsPage.optionLabels.pAttributes",
  options_tag_list: "productsPage.optionLabels.tagList",
  options_display_name: "productsPage.optionLabels.displayName",
  options_en_display_name: "productsPage.optionLabels.displayNameEn",
};

const OPTION_FIELDS = [
  // "options_product_name",
  "options_product_specs",
  "options_vender_code",
  "options_vender_name",
  "options_brand",
  "options_movie_or_portrait",
  "options_nature",
  "options_product_series",
  "options_category_name",
  "options_p_image",
  "options_p_videos",
  "options_packaging_size",
  "options_capsule_size",
  // "options_valid_until",
  // "options_mall_listing",
  "options_pr_method",
  // "options_w_attributes",
  "options_p_brand",
  // "options_p_attributes",
  "options_tag_list",
  "options_display_name",
  "options_en_display_name",
];

const hasUtf8Bom = (bytes) => UTF8_BOM_BYTES.every((value, index) => bytes[index] === value);

const SCIENTIFIC_NOTATION_PATTERN = /^[+-]?\d+(?:\.\d+)?[eE][+-]?\d+$/;

const decodeProductCsvFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const decoders = hasUtf8Bom(bytes)
    ? [new TextDecoder("utf-8")]
    : [new TextDecoder("utf-8", { fatal: true }), new TextDecoder("big5")];

  for (const decoder of decoders) {
    try {
      return decoder.decode(bytes);
    } catch (error) {
      // Fall through and try the next supported encoding.
    }
  }

  throw new Error("CSV 編碼不支援，請改用 UTF-8 或 Big5");
};

const buildUtf8CsvFile = (fileName, csvText) =>
  new File([`${UTF8_BOM}${csvText}`], fileName, { type: "text/csv;charset=utf-8" });

const INTERNAL_NOTE_BATCH_API_FIELDS = [
  "id",
  "ProductCode",
  "Barcode",
  "ProductName",
  "ProductSpecs",
  "VenderCode",
  "VenderName",
  "Brand",
  "MovieOrPortrait",
  "Nature",
  "ProductSeries",
  "CategoryName",
  "Price",
  "Description",
  "PVideos",
  "CostPrice",
  "IsEnable",
  "PackagingSize",
  "CapsuleSize",
  "ValidUntil",
  "MallListing",
  "PrMethod",
  "WAttributes",
  "PBrand",
  "PAttributes",
  "TagList",
  "NotePublic",
  "NotePrivate",
  "DisplayName",
  "EnDisplayName",
];

const OPTION_FIELD_API_KEY_MAP = {
  options_product_name: "ProductName",
  options_product_specs: "ProductSpecs",
  options_vender_code: "VenderCode",
  options_vender_name: "VenderName",
  options_brand: "Brand",
  options_movie_or_portrait: "MovieOrPortrait",
  options_nature: "Nature",
  options_product_series: "ProductSeries",
  options_category_name: "CategoryName",
  options_p_videos: "PVideos",
  options_packaging_size: "PackagingSize",
  options_capsule_size: "CapsuleSize",
  options_valid_until: "ValidUntil",
  options_mall_listing: "MallListing",
  options_pr_method: "PrMethod",
  options_w_attributes: "WAttributes",
  options_p_brand: "PBrand",
  options_p_attributes: "PAttributes",
  options_tag_list: "TagList",
  options_display_name: "DisplayName",
  options_en_display_name: "EnDisplayName",
};

const TAIPEI_TIMEZONE = "Asia/Taipei";

const formatEpochToTaipei = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const raw = typeof value === "string" ? value.trim() : value;
  const numeric = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(numeric)) return String(value);
  const ms = numeric < 1e12 ? numeric * 1000 : numeric;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TAIPEI_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

const convertToSheetData = (data = []) => {
  if (!data.length) return [];

  const headers = [
    "ProductCode",
    "Barcode",
    "ProductName",
    "ProductSpecs",
    "VenderCode",
    "VenderName",
    "Brand",
    "MovieOrPortrait",
    "Nature",
    "ProductSeries",
    "CategoryName",
    "Price",
    "NotePrivate",
    "PVideos",
    "CostPrice",
    "IsEnable",
    "PackagingSize",
    "CapsuleSize",
    "ValidUntil",
    "MallListing",
    "WAttributes",
    "PBrand",
    "PAttributes",
    "TagList",
    "Display_Name",
    "NotePublic",
    "Date_Creation",
    "Date_Modification",
  ];

  const rows = data.map((item) => {
    const opts = item.array_options || {};
    const retailPrice = parseFloat(item.price) || 0;
    const costPrice = Math.round(retailPrice * 0.8);

    return [
      item.ref ? String(item.ref) : "",
      item.barcode ? String(item.barcode) : "",
      item.label || "",
      opts.options_product_specs || "",
      opts.options_vender_code || "",
      opts.options_vender_name || "",
      opts.options_brand || "",
      opts.options_movie_or_portrait || "",
      opts.options_nature || "",
      opts.options_product_series || "",
      opts.options_category_name || "",
      item.price || "",
      item.note_private || "",
      opts.options_p_videos || "",
      costPrice,
      item.status === "1" ? "Y" : "N",
      opts.options_packaging_size || "",
      opts.options_capsule_size || "",
      opts.options_valid_until || "",
      opts.options_mall_listing || "",
      opts.options_w_attributes || "",
      opts.options_p_brand || "",
      opts.options_p_attributes || "",
      opts.options_tag_list || "",
      opts.options_display_name || "",
      item.note_public || "",
      item.date_creation || "",
      item.date_modification || "",
    ];
  });

  return [headers, ...rows];
};

const formatUnixDate = (value) => {
  const numberValue = Number(value);
  if (!numberValue || Number.isNaN(numberValue)) return value;
  const date = new Date(numberValue * 1000);
  if (Number.isNaN(date.getTime())) return value;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeMallListing = (value) => {
  if (value === true || value === "true" || value === 1 || value === "1" || value === "Y") {
    return "true";
  }
  if (value === false || value === "false" || value === 0 || value === "0" || value === "N") {
    return "false";
  }
  return "";
};

const parseMallListing = (value) => {
  if (value === "") return undefined;
  if (value === true || value === false) return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return Boolean(value);
};

const getCharCount = (str = "") => {
  let count = 0;
  for (const char of str) {
    count += char.charCodeAt(0) > 127 ? 2 : 1;
  }
  return count;
};

const normalizeOptionValueForForm = (key, value) => {
  if (key === "options_p_image") {
    if (Array.isArray(value)) {
      return value.join("\n");
    }
    return value ? String(value) : "";
  }
  if (key === "options_valid_until") {
    if (!value) return "";
    const formatted = formatUnixDate(value);
    if (typeof formatted === "string" && formatted.includes("T")) {
      return formatted.split("T")[0];
    }
    return formatted;
  }
  if (key === "options_mall_listing") {
    return normalizeMallListing(value);
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value ?? "";
};

const buildOptionFormValues = (options = {}) =>
  OPTION_FIELDS.reduce((acc, key) => {
    acc[key] = normalizeOptionValueForForm(key, options[key]);
    return acc;
  }, {});

const parseImageList = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return String(value)
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeValidUntilForUpdate = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if (trimmed.includes("T")) return trimmed;
  return `${trimmed}T00:00:00Z`;
};

const buildUpdateOptions = (formValues = {}, originalOptions = {}, originalFormValues = {}) => {
  const updated = {};
  OPTION_FIELDS.forEach((key) => {
    const value = formValues[key] ?? "";
    const originalValue = originalFormValues[key] ?? "";
    if (value === originalValue) return;
    if (key === "options_p_image") {
      updated[key] = parseImageList(value);
      return;
    }
    if (key === "options_mall_listing") {
      const parsed = parseMallListing(value);
      if (parsed !== undefined) {
        updated[key] = parsed;
      }
      return;
    }
    if (key === "options_valid_until") {
      const normalized = normalizeValidUntilForUpdate(value);
      if (normalized) {
        updated[key] = normalized;
      }
      return;
    }
    updated[key] = value;
  });
  return updated;
};

const resolveThirdPartyId = (product) => {
  if (!product) return null;
  const candidates = [
    product.third_party_id,
    product.thirdPartyId,
    product.third_partyId,
    product.socid,
    product.fk_soc,
    product.fk_socid,
  ];
  for (const value of candidates) {
    if (value === undefined || value === null || value === "") continue;
    const numberValue = Number(value);
    if (Number.isFinite(numberValue) && numberValue > 0) {
      return numberValue;
    }
  }
  const nested =
    product.third_party?.id ??
    product.third_party?.third_party_id ??
    product.thirdParty?.id ??
    product.thirdParty?.third_party_id;
  if (nested !== undefined && nested !== null && nested !== "") {
    const numberValue = Number(nested);
    if (Number.isFinite(numberValue) && numberValue > 0) {
      return numberValue;
    }
  }
  return null;
};

const InternalNoteCell = ({ row, onOpen, label }) => {
  const options = row.original.internal_note;
  if (!options || Object.keys(options).length === 0) {
    return "-";
  }
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={() => onOpen(row.original.raw)}
      sx={outlineButtonSx}
    >
      {label}
    </Button>
  );
};

InternalNoteCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      internal_note: PropTypes.object,
      raw: PropTypes.object,
    }).isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

const StatusActionCell = ({ row, onDisable, disableLabel, disabledLabel, updatingIds }) => {
  const product = row.original.raw || {};
  const status = row.original.status;
  const isDisabled = status === "0" || status === 0;
  const isUpdating = updatingIds.has(product.id);
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={() => onDisable(product)}
      sx={outlineButtonSx}
      disabled={isDisabled || isUpdating}
    >
      {isDisabled ? disabledLabel : disableLabel}
    </Button>
  );
};

StatusActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      raw: PropTypes.object,
    }).isRequired,
  }).isRequired,
  onDisable: PropTypes.func.isRequired,
  disableLabel: PropTypes.string.isRequired,
  disabledLabel: PropTypes.string.isRequired,
  updatingIds: PropTypes.instanceOf(Set).isRequired,
};

function ProductsBK() {
  const { t } = useTranslation("common");
  const isMobile = useIsMobile();
  const trigger = useScrollTrigger();
  const { userData } = useUserContext() || {};
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const backendBaseUrl = useMemo(() => resolveBackendBase(backendUrl), [backendUrl]);
  const updateApiBase = useMemo(() => `${backendBaseUrl}${UPDATE_API_PATH}`, [backendBaseUrl]);
  const batchNotePrivateApiUrl = useMemo(
    () => `${backendBaseUrl}${BATCH_NOTE_PRIVATE_API_PATH}`,
    [backendBaseUrl]
  );
  const productListUrl = useMemo(() => `${backendBaseUrl}${PRODUCT_LIST_PATH}`, [backendBaseUrl]);
  const uploadImageUrl = useMemo(() => `${backendBaseUrl}${UPLOAD_IMAGE_PATH}`, [backendBaseUrl]);
  const updateImageUrl = useMemo(() => `${backendBaseUrl}${UPDATE_IMAGE_PATH}`, [backendBaseUrl]);
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);
  const reloadTimestamp = useMemo(() => Date.now().toString(), []);
  const [imageReloadToken, setImageReloadToken] = useState(Date.now().toString());
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productError, setProductError] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const lastSelectedIdRef = useRef(null);
  const [optionForm, setOptionForm] = useState({});
  const [descriptionValue, setDescriptionValue] = useState("");
  const [notePublicValue, setNotePublicValue] = useState("");
  const [labelValue, setLabelValue] = useState("");
  const [priceValue, setPriceValue] = useState("");
  const [savingOptions, setSavingOptions] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [imageUpdateOpen, setImageUpdateOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [updateFile, setUpdateFile] = useState(null);
  const [updateIndex, setUpdateIndex] = useState("1");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [updatingImage, setUpdatingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const [imageSuccess, setImageSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [internalNoteBatchOpen, setInternalNoteBatchOpen] = useState(false);
  const [internalNoteBatchRows, setInternalNoteBatchRows] = useState([]);
  const [internalNoteBatchError, setInternalNoteBatchError] = useState("");
  const [internalNoteBatchSubmitting, setInternalNoteBatchSubmitting] = useState(false);
  const [inputMode, setInputMode] = useState("csv"); // csv | manual
  const [csvFile, setCsvFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [form, setForm] = useState({
    code: "",
    barcode: "",
    name: "",
    specs: "",
    venderCode: "",
    venderName: "",
    brand: "",
    movieOrPortrait: "",
    nature: "",
    productSeries: "",
    categoryName: "",
    price: "",
    description: "",
    pVideos: "",
    costPrice: "",
    isEnable: "1",
    packagingSize: "",
    capsuleSize: "",
    validUntil: "",
    mallListing: "N",
    prMethod: "",
    wAttributes: "",
    pBrand: "",
    pAttributes: "",
    tagList: "",
    notePublic: "",
    displayName: "",
    enDisplayName: "",
  });
  const [statusUpdatingIds, setStatusUpdatingIds] = useState(() => new Set());
  const [statusError, setStatusError] = useState("");
  const [statusSuccess, setStatusSuccess] = useState("");

  const productApiUrl = useMemo(() => {
    const base = backendBaseUrl || (typeof window !== "undefined" ? window.location.origin : "");
    const url = new URL(productListUrl, base);
    if (email) {
      url.searchParams.set("email", email);
    }
    url.searchParams.set("ts", reloadTimestamp);
    return url.toString();
  }, [backendBaseUrl, email, productListUrl, reloadTimestamp]);

  const fetchProducts = useCallback(
    async (signal) => {
      setLoadingProducts(true);
      setProductError("");
      try {
        const res = await fetch(productApiUrl, { signal });
        if (!res.ok) {
          throw new Error(`${t("productsPage.messages.loadFailed")} (${res.status})`);
        }
        const json = await res.json();
        const data = Array.isArray(json.data) ? json.data : [];
        if (!signal?.aborted) {
          setProducts(data);
        }
        return data;
      } catch (error) {
        if (error?.name === "AbortError") return [];
        if (!signal?.aborted) {
          console.error(error);
          setProductError(t("productsPage.messages.loadFailed"));
          setProducts([]);
        }
        return [];
      } finally {
        if (!signal?.aborted) {
          setLoadingProducts(false);
        }
      }
    },
    [productApiUrl, t]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchProducts(controller.signal);
    return () => controller.abort();
  }, [fetchProducts]);

  useEffect(() => {
    if (!selectedProduct) {
      setOptionForm({});
      setDescriptionValue("");
      setNotePublicValue("");
      setLabelValue("");
      setPriceValue("");
      setSaveError("");
      setSaveSuccess("");
      lastSelectedIdRef.current = null;
      return;
    }
    const currentId = selectedProduct?.id ?? null;
    const prevId = lastSelectedIdRef.current;
    lastSelectedIdRef.current = currentId;
    setOptionForm(buildOptionFormValues(selectedProduct.array_options || {}));
    setDescriptionValue(selectedProduct.description ?? "");
    setNotePublicValue(selectedProduct.note_public ?? "");
    setLabelValue(selectedProduct?.label ?? "");
    setPriceValue(selectedProduct?.price ?? "");
    setSaveError("");
    if (prevId !== currentId) {
      setSaveSuccess("");
    }
  }, [selectedProduct]);

  const originalOptionForm = useMemo(
    () => (selectedProduct ? buildOptionFormValues(selectedProduct.array_options || {}) : {}),
    [selectedProduct]
  );
  const originalDescription = useMemo(() => selectedProduct?.description ?? "", [selectedProduct]);
  const originalNotePublic = useMemo(() => selectedProduct?.note_public ?? "", [selectedProduct]);
  const originalLabel = useMemo(() => selectedProduct?.label ?? "", [selectedProduct]);
  const originalPrice = useMemo(() => selectedProduct?.price ?? "", [selectedProduct]);

  const isOptionDirty = useMemo(() => {
    if (!selectedProduct) return false;
    return OPTION_FIELDS.some((key) => (optionForm[key] ?? "") !== (originalOptionForm[key] ?? ""));
  }, [optionForm, originalOptionForm, selectedProduct]);
  const isDescriptionDirty = useMemo(
    () => descriptionValue !== originalDescription,
    [descriptionValue, originalDescription]
  );
  const isNotePublicDirty = useMemo(
    () => notePublicValue !== originalNotePublic,
    [notePublicValue, originalNotePublic]
  );
  const isLabelDirty = useMemo(() => labelValue !== originalLabel, [labelValue, originalLabel]);
  const isPriceDirty = useMemo(() => priceValue !== originalPrice, [priceValue, originalPrice]);
  const isFormDirty =
    isOptionDirty || isDescriptionDirty || isNotePublicDirty || isLabelDirty || isPriceDirty;

  const handleOpenDetail = useCallback((product) => {
    setSelectedProduct(product);
    setDetailOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
    setSelectedProduct(null);
  }, []);

  const handleOptionChange = useCallback(
    (key, value) => {
      setOptionForm((prev) => ({ ...prev, [key]: value }));
      if (saveSuccess) setSaveSuccess("");
    },
    [saveSuccess]
  );

  const handleCloseSaveToast = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setSaveSuccess("");
  }, []);

  const handleSaveOptions = useCallback(async () => {
    if (!selectedProduct?.id) {
      setSaveError(t("productsPage.messages.updateNeedId"));
      return;
    }
    if (!email) {
      setSaveError(t("productsPage.messages.updateNeedEmail"));
      return;
    }
    setSavingOptions(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const updatedOptions = buildUpdateOptions(
        optionForm,
        selectedProduct.array_options || {},
        originalOptionForm
      );
      if (
        Object.prototype.hasOwnProperty.call(updatedOptions, "options_mall_listing") &&
        typeof updatedOptions.options_mall_listing === "string"
      ) {
        const normalized = updatedOptions.options_mall_listing.trim().toLowerCase();
        updatedOptions.options_mall_listing =
          normalized === "true" || normalized === "1" || normalized === "y";
      }
      const requestUrl = `${updateApiBase}/${selectedProduct.id}`;
      const requestBody = {
        email,
        description: descriptionValue,
        note_public: notePublicValue,
        array_options: updatedOptions,
        label: labelValue,
      };
      if (isPriceDirty) {
        requestBody.price = priceValue;
      }
      const thirdPartyId = resolveThirdPartyId(selectedProduct);
      if (thirdPartyId) {
        requestBody.third_party_id = thirdPartyId;
      }
      const mergedOptions = {
        ...(selectedProduct.array_options || {}),
        ...updatedOptions,
      };
      console.info("[Product Update] request", {
        url: requestUrl,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: requestBody,
      });

      const res = await fetch(requestUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        console.error("[Product Update] response", res.status, errorText);
        const extra = errorText ? ` ${errorText}` : "";
        throw new Error(`${t("productsPage.messages.updateFailed")} (${res.status})${extra}`);
      }

      let updatedProduct = {
        ...selectedProduct,
        description: descriptionValue,
        note_public: notePublicValue,
        array_options: mergedOptions,
        label: labelValue,
        price: priceValue,
      };

      try {
        const json = await res.json();
        if (json && typeof json === "object" && json.data && typeof json.data === "object") {
          updatedProduct = {
            ...updatedProduct,
            ...json.data,
            array_options: json.data.array_options || mergedOptions,
          };
        }
      } catch (error) {
        console.warn("Update API response is not JSON.", error);
      }

      setProducts((prev) =>
        prev.map((item) => (item.id === updatedProduct.id ? updatedProduct : item))
      );
      setSelectedProduct(updatedProduct);
      setSaveSuccess(t("productsPage.messages.updateSuccess"));
    } catch (error) {
      console.error(error);
      setSaveError(t("productsPage.messages.updateFailed"));
    } finally {
      setSavingOptions(false);
    }
  }, [
    descriptionValue,
    email,
    notePublicValue,
    labelValue,
    priceValue,
    optionForm,
    originalOptionForm,
    isPriceDirty,
    selectedProduct,
    t,
    updateApiBase,
  ]);

  const resetImageMessages = useCallback(() => {
    setImageError("");
    setImageSuccess("");
  }, []);

  const handleOpenUploadDialog = useCallback(() => {
    setImageUploadOpen(true);
    setUploadFiles([]);
    resetImageMessages();
  }, [resetImageMessages]);

  const handleCloseUploadDialog = useCallback(() => {
    setImageUploadOpen(false);
    setUploadFiles([]);
    resetImageMessages();
  }, [resetImageMessages]);

  const handleOpenUpdateDialog = useCallback(() => {
    setImageUpdateOpen(true);
    setUpdateFile(null);
    resetImageMessages();
  }, [resetImageMessages]);

  const handleCloseUpdateDialog = useCallback(() => {
    setImageUpdateOpen(false);
    setUpdateFile(null);
    resetImageMessages();
  }, [resetImageMessages]);

  const updateStatusUpdatingIds = useCallback((productId, isUpdating) => {
    if (!productId) return;
    setStatusUpdatingIds((prev) => {
      const next = new Set(prev);
      if (isUpdating) {
        next.add(productId);
      } else {
        next.delete(productId);
      }
      return next;
    });
  }, []);

  const handleDisableProduct = useCallback(
    async (product) => {
      if (!product?.id) {
        setStatusError(t("productsPage.messages.updateNeedId"));
        return;
      }
      if (!email) {
        setStatusError(t("productsPage.messages.updateNeedEmail"));
        return;
      }
      const isDisabled = product.status === "0" || product.status === 0;
      if (isDisabled) return;

      updateStatusUpdatingIds(product.id, true);
      setStatusError("");
      setStatusSuccess("");

      try {
        const requestUrl = `${updateApiBase}/${product.id}`;
        const requestBody = { email, status: 0 };
        const thirdPartyId = resolveThirdPartyId(product);
        if (thirdPartyId) {
          requestBody.third_party_id = thirdPartyId;
        }
        const res = await fetch(requestUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });
        if (!res.ok) {
          const errorText = await res.text().catch(() => "");
          const extra = errorText ? ` ${errorText}` : "";
          throw new Error(`${t("productsPage.messages.disableFailed")} (${res.status})${extra}`);
        }

        let updatedProduct = { ...product, status: 0 };
        try {
          const json = await res.json();
          if (json && typeof json === "object" && json.data && typeof json.data === "object") {
            updatedProduct = { ...updatedProduct, ...json.data };
          }
        } catch (error) {
          console.warn("Disable status response is not JSON.", error);
        }

        setProducts((prev) =>
          prev.map((item) =>
            item.id === updatedProduct.id ? { ...item, ...updatedProduct } : item
          )
        );
        if (selectedProduct?.id === updatedProduct.id) {
          setSelectedProduct((prev) => (prev ? { ...prev, ...updatedProduct } : prev));
        }
        setStatusSuccess(t("productsPage.messages.disableSuccess"));
      } catch (error) {
        console.error(error);
        setStatusError(t("productsPage.messages.disableFailed"));
      } finally {
        updateStatusUpdatingIds(product.id, false);
      }
    },
    [email, selectedProduct, t, updateApiBase, updateStatusUpdatingIds]
  );

  const refreshSelectedProduct = useCallback(
    async (productId) => {
      const data = await fetchProducts();
      if (!data.length || !productId) return;
      const updated = data.find((item) => item.id === productId);
      if (updated) {
        setSelectedProduct(updated);
      }
    },
    [fetchProducts]
  );

  const handleUploadImages = useCallback(async () => {
    if (!selectedProduct?.id) {
      setImageError(t("productsPage.messages.uploadNeedId"));
      return;
    }
    if (!uploadFiles.length) {
      setImageError(t("productsPage.messages.uploadNeedFile"));
      return;
    }

    setUploadingImage(true);
    resetImageMessages();

    try {
      const formData = new FormData();
      uploadFiles.forEach((file) => {
        formData.append("file", file);
      });
      formData.append("product_id", String(selectedProduct.id));

      const res = await fetch(uploadImageUrl, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        throw new Error(`${t("productsPage.messages.uploadFailed")} (${res.status})`);
      }

      await refreshSelectedProduct(selectedProduct.id);
      setImageReloadToken(Date.now().toString());
      setImageSuccess(t("productsPage.messages.uploadSuccess"));
    } catch (error) {
      console.error(error);
      setImageError(t("productsPage.messages.uploadFailed"));
    } finally {
      setUploadingImage(false);
    }
  }, [refreshSelectedProduct, resetImageMessages, selectedProduct, t, uploadFiles, uploadImageUrl]);

  const handleUpdateImage = useCallback(async () => {
    if (!selectedProduct?.id) {
      setImageError(t("productsPage.messages.updateNeedId"));
      return;
    }
    if (!selectedProduct?.barcode) {
      setImageError(t("productsPage.messages.updateImageNeedBarcode"));
      return;
    }
    if (!updateFile) {
      setImageError(t("productsPage.messages.updateImageNeedFile"));
      return;
    }
    if (!updateIndex) {
      setImageError(t("productsPage.messages.updateImageNeedIndex"));
      return;
    }

    setUpdatingImage(true);
    resetImageMessages();

    try {
      const formData = new FormData();
      formData.append("file", updateFile);
      formData.append("product_id", String(selectedProduct.id));
      formData.append("object_name", `${selectedProduct.barcode}-${updateIndex}.jpg`);

      const res = await fetch(updateImageUrl, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        throw new Error(`${t("productsPage.messages.updateImageFailed")} (${res.status})`);
      }

      await refreshSelectedProduct(selectedProduct.id);
      setImageReloadToken(Date.now().toString());
      setImageSuccess(t("productsPage.messages.updateImageSuccess"));
    } catch (error) {
      console.error(error);
      setImageError(t("productsPage.messages.updateImageFailed"));
    } finally {
      setUpdatingImage(false);
    }
  }, [
    refreshSelectedProduct,
    resetImageMessages,
    selectedProduct,
    t,
    updateFile,
    updateIndex,
    updateImageUrl,
  ]);

  const resetInternalNoteBatchState = () => {
    setInternalNoteBatchRows([]);
    setInternalNoteBatchError("");
    setInternalNoteBatchSubmitting(false);
  };

  const handleOpenInternalNoteBatchDialog = () => {
    resetInternalNoteBatchState();
    setInternalNoteBatchOpen(true);
  };

  const handleCloseInternalNoteBatchDialog = () => {
    setInternalNoteBatchOpen(false);
    resetInternalNoteBatchState();
  };

  const escapeCsvValue = (value) => {
    const raw = value === null || value === undefined ? "" : String(value);
    if (/["\n,]/.test(raw)) {
      return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
  };

  const downloadInternalNoteBatchTemplate = () => {
    const headers = [
      "Barcode",
      ...OPTION_FIELDS.map((key) => (OPTION_LABELS[key] ? t(OPTION_LABELS[key]) : key)),
    ];
    const values = ["必填", ...OPTION_FIELDS.map(() => "")];
    const csv = `${headers.map(escapeCsvValue).join(",")}\n${values.join(",")}\n`;
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "internal_note_batch_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const normalizeCsvHeader = (value) =>
    String(value || "")
      .replace(/^\uFEFF/, "")
      .trim();

  const normalizeCsvValue = (value) => String(value ?? "").trim();

  const isInternalNoteTemplateHintRow = (row) => normalizeCsvValue(row.Barcode) === "必填";

  const mapInternalNoteBatchRows = (rowsJson) => {
    const optionHeaderMap = OPTION_FIELDS.reduce((acc, key) => {
      const apiKey = OPTION_FIELD_API_KEY_MAP[key];
      if (!apiKey) return acc;
      return {
        ...acc,
        [normalizeCsvHeader(OPTION_LABELS[key] ? t(OPTION_LABELS[key]) : key)]: apiKey,
      };
    }, {});
    const apiHeaderMap = INTERNAL_NOTE_BATCH_API_FIELDS.reduce(
      (acc, key) => ({ ...acc, [normalizeCsvHeader(key)]: key }),
      {}
    );
    const headerMap = { ...apiHeaderMap, ...optionHeaderMap };

    return rowsJson.map((row) =>
      Object.entries(row).reduce((acc, [header, value]) => {
        const mappedKey = headerMap[normalizeCsvHeader(header)];
        if (!mappedKey) return acc;
        acc[mappedKey] = normalizeCsvValue(value);
        return acc;
      }, {})
    );
  };

  const validateInternalNoteBatchRows = (rows) => {
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      if (!normalizeCsvValue(row.Barcode)) {
        return `第 ${i + 1} 列 Barcode 必填`;
      }
    }
    return "";
  };

  const handleInternalNoteCsvUpload = async (file) => {
    if (!file) return;
    resetInternalNoteBatchState();
    try {
      const csvText = await decodeProductCsvFile(file);
      const workbook = XLSX.read(csvText, { type: "string", raw: true });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        setInternalNoteBatchError("CSV 內容為空");
        return;
      }

      const sheet = workbook.Sheets[sheetName];
      const rowsJson = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
      if (!rowsJson.length) {
        setInternalNoteBatchError("CSV 沒有資料");
        return;
      }

      const mappedRows = mapInternalNoteBatchRows(rowsJson).filter(
        (row) => !isInternalNoteTemplateHintRow(row)
      );
      if (!mappedRows.length) {
        setInternalNoteBatchError("CSV 沒有資料");
        return;
      }

      const validationError = validateInternalNoteBatchRows(mappedRows);
      if (validationError) {
        setInternalNoteBatchError(validationError);
        return;
      }

      setInternalNoteBatchRows(mappedRows);
    } catch (error) {
      setInternalNoteBatchError(error.message || "CSV 讀取失敗");
    }
  };

  const buildInternalNoteBatchCsvFile = (rows) => {
    const headers = INTERNAL_NOTE_BATCH_API_FIELDS.filter((key) =>
      rows.some((row) => Object.prototype.hasOwnProperty.call(row, key))
    );
    const csvRows = rows.map((row) => headers.map((key) => escapeCsvValue(row[key])).join(","));
    const csv = `${headers.map(escapeCsvValue).join(",")}\n${csvRows.join("\n")}\n`;
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    return new File([blob], "internal_note_batch.csv", { type: "text/csv" });
  };

  const handleSubmitInternalNoteBatch = async () => {
    if (!email) {
      setInternalNoteBatchError(t("productsPage.messages.updateNeedEmail"));
      return;
    }
    if (!internalNoteBatchRows.length) {
      setInternalNoteBatchError("沒有可上傳的資料");
      return;
    }

    setInternalNoteBatchSubmitting(true);
    setInternalNoteBatchError("");

    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("file", buildInternalNoteBatchCsvFile(internalNoteBatchRows));

      const res = await fetch(batchNotePrivateApiUrl, {
        method: "PUT",
        headers: {
          "Mdm-Auth": mdmAuthKey,
        },
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        throw new Error(errorText || `Request failed (${res.status})`);
      }

      setStatusSuccess("內部註解批次更新成功");
      handleCloseInternalNoteBatchDialog();
      fetchProducts();
    } catch (error) {
      console.error(error);
      setInternalNoteBatchError(error.message || "批次更新失敗");
    } finally {
      setInternalNoteBatchSubmitting(false);
    }
  };

  const handleOpenDialog = () => setOpenDialog(true);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCsvFile(null);
    setUploadError("");
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleChangeWithLimit = (e) => {
    const value = e.target.value;
    if (getCharCount(value) <= 100) {
      handleChange(e);
    }
  };

  const validateProductCsvRows = (csvText) => {
    const workbook = XLSX.read(csvText, { type: "string", raw: true });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return "CSV 內容為空";
    }

    const sheet = workbook.Sheets[sheetName];
    const rowsJson = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
    if (!rowsJson.length) {
      return "CSV 沒有資料";
    }

    for (let i = 0; i < rowsJson.length; i += 1) {
      const row = rowsJson[i];
      const productCode = normalizeCsvValue(row.ProductCode);
      const barcode = normalizeCsvValue(row.Barcode);
      const displayName = normalizeCsvValue(row.DisplayName);
      const enDisplayName = normalizeCsvValue(row.EnDisplayName);

      if (SCIENTIFIC_NOTATION_PATTERN.test(productCode)) {
        return `第 ${
          i + 1
        } 列 ProductCode 是科學記號 ${productCode}，請將欄位改成文字格式後重新匯出 CSV`;
      }
      if (SCIENTIFIC_NOTATION_PATTERN.test(barcode)) {
        return `第 ${i + 1} 列 Barcode 是科學記號 ${barcode}，請將欄位改成文字格式後重新匯出 CSV`;
      }
      if (getCharCount(displayName) > 100) {
        return `第 ${i + 1} 列 DisplayName 超過100字元`;
      }
      if (getCharCount(enDisplayName) > 100) {
        return `第 ${i + 1} 列 EnDisplayName 超過100字元`;
      }
    }

    return "";
  };

  // useEffect(() => {
  //   console.log(csvFile);
  // }, [csvFile]);

  const handleSubmit = async () => {
    setUploadError("");
    const formData = new FormData();
    formData.append("email", email);

    if (inputMode === "csv" && csvFile) {
      let csvText = "";
      try {
        csvText = await decodeProductCsvFile(csvFile);
        const validationError = validateProductCsvRows(csvText);
        if (validationError) {
          setUploadError(validationError);
          return;
        }
      } catch (error) {
        setUploadError(error.message || "CSV 讀取失敗");
        return;
      }
      console.log("[Product CSV Upload] file content", csvText);
      formData.append("file", buildUtf8CsvFile(csvFile.name, csvText));
    } else {
      // 手動填寫 → 轉成 CSV
      const csvContent = [
        [
          "ProductCode",
          "Barcode",
          "ProductName",
          "ProductSpecs",
          "VenderCode",
          "VenderName",
          "Brand",
          "MovieOrPortrait",
          "Nature",
          "ProductSeries",
          "CategoryName",
          "Price",
          "Description",
          "PVideos",
          "CostPrice",
          "IsEnable",
          "PackagingSize",
          "CapsuleSize",
          "ValidUntil",
          "MallListing",
          "PrMethod",
          "WAttributes",
          "PBrand",
          "PAttributes",
          "TagList",
          "DisplayName",
          "EnDisplayName",
          "NotePublic",
        ],
        [
          form.code,
          form.barcode,
          form.name,
          form.specs,
          form.venderCode,
          form.venderName,
          form.brand,
          form.movieOrPortrait,
          form.nature,
          form.productSeries,
          form.categoryName,
          form.price,
          form.description,
          form.pVideos,
          form.costPrice,
          form.isEnable,
          form.packagingSize,
          form.capsuleSize,
          form.validUntil,
          form.mallListing,
          form.prMethod,
          form.wAttributes,
          form.pBrand,
          form.pAttributes,
          form.tagList,
          form.displayName,
          form.enDisplayName,
          form.notePublic,
        ],
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      formData.append("file", blob, "product.csv");
    }

    try {
      const response = await fetch(
        "https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/product/create",
        {
          method: "POST",
          headers: {
            "Mdm-Auth": mdmAuthKey,
          },
          body: formData,
        }
      );

      const result = await response.json();
      console.log(result);

      if (result?.data?.failed_details && result.data.failed_details.length > 0) {
        const failedMessages = result.data.failed_details
          .map((item) => `${item.product_code}: ${item.reason}`)
          .join("\n");
        alert(`上傳部分失敗：\n${failedMessages}`);
      } else {
        alert("上傳成功！");
      }
    } catch (error) {
      alert("上傳失敗：" + error.message);
    }

    // reset
    setForm({
      code: "",
      barcode: "",
      name: "",
      specs: "",
      venderCode: "",
      venderName: "",
      brand: "",
      movieOrPortrait: "",
      nature: "",
      productSeries: "",
      categoryName: "",
      price: "",
      description: "",
      pVideos: "",
      costPrice: "",
      isEnable: "1",
      packagingSize: "",
      capsuleSize: "",
      validUntil: "",
      mallListing: "N",
      prMethod: "",
      wAttributes: "",
      pBrand: "",
      pAttributes: "",
      tagList: "",
      notePublic: "",
      displayName: "",
      enDisplayName: "",
    });
    // setCsvFile(null);
    // setOpenDialog(false);
  };

  const productRows = useMemo(
    () =>
      products.map((item) => {
        const options = item.array_options || {};
        return {
          internal_note: options,
          // id: item.id ?? "",
          barcode: item.barcode ?? "",
          ref: item.ref ?? "",
          label: item.label ?? "",
          price: item.price ?? "",
          stock_reel: item.stock_reel ?? "",
          note_public: item.note_public ?? "",
          // note_private: item.note_private ?? "",
          status: item.status ?? "",
          status_buy: item.status_buy ?? "",
          display_name: options.options_display_name ?? "",
          en_display_name: options.options_en_display_name ?? "",
          date_creation: item.date_creation ?? "",
          date_modification: item.date_modification ?? "",
          raw: item,
        };
      }),
    [products]
  );

  const productColumns = useMemo(
    () => [
      {
        Header: t("productsPage.table.internalNote"),
        accessor: "internal_note",
        Cell: (props) => (
          <InternalNoteCell
            {...props}
            onOpen={handleOpenDetail}
            label={t("productsPage.actions.viewInternalNote")}
          />
        ),
      },
      // { Header: t("productsPage.table.id"), accessor: "id" },
      { Header: t("productsPage.table.barcode"), accessor: "barcode" },
      { Header: t("productsPage.table.ref"), accessor: "ref" },
      { Header: t("productsPage.table.label"), accessor: "label" },
      { Header: t("productsPage.table.price"), accessor: "price" },
      { Header: t("productsPage.table.stock"), accessor: "stock_reel" },
      { Header: t("productsPage.table.notePublic"), accessor: "note_public" },
      // { Header: t("productsPage.table.notePrivate"), accessor: "note_private" },
      {
        Header: t("productsPage.table.status"),
        accessor: "status",
        Cell: ({ value }) =>
          value === "1" || value === 1
            ? t("productsPage.status.enabled")
            : t("productsPage.status.disabled"),
      },
      {
        Header: t("productsPage.table.statusBuy"),
        accessor: "status_buy",
        Cell: ({ value }) =>
          value === "1" || value === 1
            ? t("productsPage.status.enabled")
            : t("productsPage.status.disabled"),
      },
      {
        Header: t("productsPage.table.actions"),
        accessor: "actions",
        Cell: (props) => (
          <StatusActionCell
            {...props}
            onDisable={handleDisableProduct}
            disableLabel={t("productsPage.actions.disable")}
            disabledLabel={t("productsPage.actions.disabled")}
            updatingIds={statusUpdatingIds}
          />
        ),
      },
      { Header: t("productsPage.table.displayName"), accessor: "display_name" },
      { Header: t("productsPage.table.enDisplayName"), accessor: "en_display_name" },

      {
        Header: t("productsPage.table.createdAt"),
        accessor: "date_creation",
        Cell: ({ value }) => formatEpochToTaipei(value),
      },
      {
        Header: t("productsPage.table.updatedAt"),
        accessor: "date_modification",
        Cell: ({ value }) => formatEpochToTaipei(value),
      },
    ],
    [handleDisableProduct, handleOpenDetail, statusUpdatingIds, t]
  );

  const selectedOptions = selectedProduct?.array_options || {};
  const productImages = useMemo(() => {
    const source =
      optionForm.options_p_image === undefined
        ? selectedOptions.options_p_image
        : optionForm.options_p_image;
    return parseImageList(source);
  }, [optionForm.options_p_image, selectedOptions.options_p_image]);
  const updateSlots = useMemo(() => {
    const count = Math.min(8, productImages.length);
    if (!count) return [];
    return Array.from({ length: count }, (_, index) => `${index + 1}`);
  }, [productImages.length]);

  useEffect(() => {
    if (!updateSlots.length) {
      setUpdateIndex("1");
      return;
    }
    if (!updateSlots.includes(updateIndex)) {
      setUpdateIndex(updateSlots[0]);
    }
  }, [updateIndex, updateSlots]);

  const currentUpdateImage = updateSlots.length > 0 ? productImages[Number(updateIndex) - 1] : null;

  const downloadExcel = async () => {
    if (!email) {
      alert(t("productsPage.messages.downloadNeedEmail"));
      return;
    }

    try {
      const res = await fetch(productApiUrl);
      const json = await res.json();
      if (!json.data) {
        alert(t("productsPage.messages.downloadNoData"));
        return;
      }

      const sheetData = convertToSheetData(json.data);
      if (!sheetData.length) {
        alert(t("productsPage.messages.downloadEmpty"));
        return;
      }

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      const range = XLSX.utils.decode_range(worksheet["!ref"]);

      for (let row = 1; row <= range.e.r; row += 1) {
        const productCodeCell = worksheet[XLSX.utils.encode_cell({ r: row, c: 0 })];
        const barcodeCell = worksheet[XLSX.utils.encode_cell({ r: row, c: 1 })];
        if (productCodeCell) productCodeCell.t = "s";
        if (barcodeCell) barcodeCell.t = "s";
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
      XLSX.writeFile(workbook, "products.xlsx");
    } catch (error) {
      console.error(error);
      alert(t("productsPage.messages.downloadFailed"));
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={isMobile ? 2 : 3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.products")}
                </MDTypography>
                <MDBox display={isMobile ? "none" : "flex"} alignItems="center" gap={2}>
                  <Button
                    id="內部註解批次編輯"
                    variant="contained"
                    onClick={handleOpenInternalNoteBatchDialog}
                    sx={{
                      backgroundColor: "#FF9800",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#FB8C00" },
                    }}
                  >
                    內部註解批次編輯
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleOpenDialog}
                    sx={{
                      backgroundColor: "#FF9800",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#FB8C00" },
                    }}
                  >
                    {t("actions.createProduct")}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={downloadExcel}
                    sx={{
                      backgroundColor: "#FF9800",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#FB8C00" },
                    }}
                  >
                    {t("actions.exportProductSheet")}
                  </Button>
                </MDBox>
              </MDBox>

              <MDBox pt={3} px={0} width="100%">
                {loadingProducts && (
                  <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
                    <CircularProgress />
                  </MDBox>
                )}
                {!loadingProducts && productError && (
                  <MDBox py={3} px={3}>
                    <MDTypography variant="body2" color="error">
                      {productError}
                    </MDTypography>
                  </MDBox>
                )}
                {!loadingProducts && !productError && (
                  <DataTable
                    table={{ columns: productColumns, rows: productRows }}
                    entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                    canSearch
                    showTotalEntries={false}
                    isSorted
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={internalNoteBatchOpen}
        onClose={handleCloseInternalNoteBatchDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>內部註解批次編輯</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {internalNoteBatchError && (
            <MDTypography color="error" sx={{ mb: 1 }}>
              {internalNoteBatchError}
            </MDTypography>
          )}
          <MDBox mt={1}>
            <MDTypography variant="subtitle2" sx={{ mb: 1 }}>
              批次上傳 CSV
            </MDTypography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                onClick={downloadInternalNoteBatchTemplate}
                sx={{ color: "#1A73E8", borderColor: "#1A73E8" }}
              >
                下載範本
              </Button>
              <Button
                variant="outlined"
                component="label"
                sx={{ color: "#1A73E8", borderColor: "#1A73E8" }}
              >
                上傳 CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(event) => {
                    handleInternalNoteCsvUpload(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
              </Button>
              {internalNoteBatchRows.length > 0 && (
                <MDTypography variant="caption">
                  已載入 {internalNoteBatchRows.length} 筆
                </MDTypography>
              )}
            </Stack>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInternalNoteBatchDialog}>取消</Button>
          <Button
            variant="contained"
            onClick={handleSubmitInternalNoteBatch}
            disabled={internalNoteBatchSubmitting || !internalNoteBatchRows.length}
            sx={{
              backgroundColor: "#5B3FD1",
              color: "#FFFFFF",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#4A33B0", boxShadow: "none" },
            }}
          >
            {internalNoteBatchSubmitting ? "送出中..." : "送出"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="lg">
        <DialogTitle>{selectedProduct?.label || t("productsPage.detail.title")}</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <MDBox>
            <MDTypography variant="subtitle2" fontWeight="medium" gutterBottom>
              {t("productsPage.detail.basicInfo")}
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography variant="button" color="text">
                  {t("productsPage.detail.ref")}: {selectedProduct?.ref || "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography variant="button" color="text">
                  {t("productsPage.detail.barcode")}: {selectedProduct?.barcode || "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography variant="button" color="text">
                  {t("productsPage.detail.displayName")}:{" "}
                  {optionForm.options_display_name || selectedOptions.options_display_name || "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography variant="button" color="text">
                  {t("productsPage.detail.price")}: {priceValue || "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography variant="button" color="text">
                  {t("productsPage.detail.stock")}: {selectedProduct?.stock_reel || "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography variant="button" color="text">
                  {t("productsPage.detail.status")}:{" "}
                  {selectedProduct?.status === "1" || selectedProduct?.status === 1
                    ? t("productsPage.status.enabled")
                    : t("productsPage.status.disabled")}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <MDTypography variant="button" color="text">
                  {t("productsPage.detail.statusBuy")}:{" "}
                  {selectedProduct?.status_buy === "1" || selectedProduct?.status_buy === 1
                    ? t("productsPage.status.enabled")
                    : t("productsPage.status.disabled")}
                </MDTypography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("productsPage.detail.description")}
                  value={descriptionValue}
                  onChange={(event) => {
                    setDescriptionValue(event.target.value);
                    if (saveSuccess) setSaveSuccess("");
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </MDBox>

          <MDBox>
            <MDTypography variant="subtitle2" fontWeight="medium" gutterBottom>
              {t("productsPage.images.title")}
            </MDTypography>
            <MDBox display="flex" gap={2} mb={2} flexWrap="wrap">
              <Button variant="outlined" onClick={handleOpenUploadDialog} sx={outlineButtonSx}>
                {t("productsPage.images.add")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenUpdateDialog}
                disabled={!productImages.length}
                sx={outlineButtonSx}
              >
                {t("productsPage.images.update")}
              </Button>
            </MDBox>
            {productImages.length > 0 ? (
              <MDBox
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                }}
                gap={2}
              >
                {productImages.map((url, index) => (
                  <MDBox
                    key={`${url}-${index}`}
                    component="img"
                    src={`${url}?reload=${imageReloadToken}`}
                    alt={`product-${index}`}
                    sx={{
                      width: "100%",
                      height: 140,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  />
                ))}
              </MDBox>
            ) : (
              <MDTypography variant="button" color="text">
                {t("productsPage.images.empty")}
              </MDTypography>
            )}
          </MDBox>

          <Divider />

          <MDBox>
            <MDTypography variant="subtitle2" fontWeight="medium" gutterBottom>
              {t("productsPage.internalNotes.title")}
            </MDTypography>
            <MDBox mb={2}>
              <TextField
                label="建檔備註"
                value={notePublicValue}
                onChange={(event) => setNotePublicValue(event.target.value)}
                fullWidth
                size="small"
                multiline
                rows={3}
              />
            </MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("productsPage.optionLabels.productName")}
                  value={labelValue}
                  onChange={(event) => setLabelValue(event.target.value)}
                  fullWidth
                  size="small"
                  rows={1}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("productsPage.detail.price")}
                  value={priceValue}
                  onChange={(event) => {
                    setPriceValue(event.target.value);
                    if (saveSuccess) setSaveSuccess("");
                  }}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              {OPTION_FIELDS.map((key) => {
                const label = OPTION_LABELS[key] ? t(OPTION_LABELS[key]) : key;
                const value = optionForm[key] ?? "";
                if (key === "options_mall_listing") {
                  return (
                    <Grid item xs={12} md={6} key={key}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{label}</InputLabel>
                        <Select
                          value={value}
                          label={label}
                          onChange={(event) => handleOptionChange(key, event.target.value)}
                          sx={selectSx}
                        >
                          <MenuItem value="">{t("productsPage.mallListing.unset")}</MenuItem>
                          <MenuItem value="true">{t("productsPage.mallListing.yes")}</MenuItem>
                          <MenuItem value="false">{t("productsPage.mallListing.no")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                }

                const isImageField = key === "options_p_image";
                const isVideoField = key === "options_p_videos";
                const isTagField = key === "options_tag_list";
                const isValidUntilField = key === "options_valid_until";
                const multiline = isImageField || isVideoField || isTagField;

                return (
                  <Grid item xs={12} md={6} key={key}>
                    <TextField
                      label={label}
                      value={value}
                      onChange={(event) => handleOptionChange(key, event.target.value)}
                      fullWidth
                      size="small"
                      multiline={multiline}
                      rows={multiline ? 3 : 1}
                      type={isValidUntilField ? "date" : "text"}
                      InputLabelProps={isValidUntilField ? { shrink: true } : undefined}
                    />
                  </Grid>
                );
              })}
            </Grid>

            {(saveError || saveSuccess) && (
              <MDBox mt={2}>
                {saveError && (
                  <MDTypography variant="button" color="error">
                    {saveError}
                  </MDTypography>
                )}
                {saveSuccess && (
                  <MDTypography variant="button" color="success">
                    {saveSuccess}
                  </MDTypography>
                )}
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDetail}>{t("productsPage.actions.close")}</Button>
          <Button
            variant="contained"
            onClick={handleSaveOptions}
            disabled={savingOptions || !selectedProduct || !isFormDirty}
            sx={primaryButtonSx}
          >
            {savingOptions
              ? t("productsPage.internalNotes.updating")
              : t("productsPage.internalNotes.update")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageUploadOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t("productsPage.images.uploadTitle")}</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <MDTypography variant="button" color="text">
            {t("productsPage.images.uploadHint")}
          </MDTypography>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => setUploadFiles(Array.from(event.target.files || []))}
          />
          {uploadFiles.length > 0 && (
            <MDTypography variant="button" color="text">
              {t("productsPage.images.selectedFiles", { count: uploadFiles.length })}
            </MDTypography>
          )}
          {(imageError || imageSuccess) && (
            <MDBox>
              {imageError && (
                <MDTypography variant="button" color="error">
                  {imageError}
                </MDTypography>
              )}
              {imageSuccess && (
                <MDTypography variant="button" color="success">
                  {imageSuccess}
                </MDTypography>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseUploadDialog}>{t("productsPage.actions.close")}</Button>
          <Button
            variant="contained"
            onClick={handleUploadImages}
            disabled={uploadingImage}
            sx={primaryButtonSx}
          >
            {uploadingImage
              ? t("productsPage.actions.uploading")
              : t("productsPage.actions.startUpload")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageUpdateOpen} onClose={handleCloseUpdateDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t("productsPage.images.updateTitle")}</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth size="small" disabled={!updateSlots.length}>
            <InputLabel>{t("productsPage.images.indexLabel")}</InputLabel>
            <Select
              value={updateSlots.length ? updateIndex : ""}
              label={t("productsPage.images.indexLabel")}
              onChange={(event) => setUpdateIndex(event.target.value)}
              sx={selectSx}
            >
              {updateSlots.map((slot) => (
                <MenuItem key={slot} value={slot}>
                  {t("productsPage.images.indexItem", { slot })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {currentUpdateImage && (
            <MDBox
              component="img"
              src={`${currentUpdateImage}?reload=${imageReloadToken}`}
              alt="current-product"
              sx={{
                width: "100%",
                height: 180,
                objectFit: "cover",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
              }}
            />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(event) => setUpdateFile(event.target.files?.[0] || null)}
          />
          {(imageError || imageSuccess) && (
            <MDBox>
              {imageError && (
                <MDTypography variant="button" color="error">
                  {imageError}
                </MDTypography>
              )}
              {imageSuccess && (
                <MDTypography variant="button" color="success">
                  {imageSuccess}
                </MDTypography>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseUpdateDialog}>{t("productsPage.actions.close")}</Button>
          <Button
            variant="contained"
            onClick={handleUpdateImage}
            disabled={updatingImage || !updateSlots.length}
            sx={primaryButtonSx}
          >
            {updatingImage
              ? t("productsPage.actions.updatingImage")
              : t("productsPage.actions.updateImage")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(saveSuccess)}
        autoHideDuration={null}
        onClose={handleCloseSaveToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSaveToast}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {saveSuccess}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{t("productForm.title")}</DialogTitle>
        <DialogContent>
          <Tabs
            value={inputMode}
            onChange={(e, v) => {
              setInputMode(v);
              setUploadError("");
            }}
            sx={{ mb: 2 }}
          >
            <Tab label={t("productForm.tabs.csv")} value="csv" />
            <Tab label={t("productForm.tabs.manual")} value="manual" />
          </Tabs>

          {inputMode === "csv" && (
            <Box mt={2}>
              <Typography variant="subtitle2">{t("productForm.uploadCsvLabel")}</Typography>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => {
                  setCsvFile(e.target.files?.[0] || null);
                  setUploadError("");
                }}
                style={{ marginTop: 8 }}
              />
              {csvFile && (
                <Typography variant="body2" color="primary">
                  {t("productForm.selectedFile", { fileName: csvFile.name })}
                </Typography>
              )}
              {uploadError && (
                <MDTypography color="error" sx={{ mt: 1 }}>
                  {uploadError}
                </MDTypography>
              )}
            </Box>
          )}

          {inputMode === "manual" && (
            <Grid container spacing={2}>
              {/* 必填 */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.code")}
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.barcode")}
                  name="barcode"
                  value={form.barcode}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.name")}
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.displayName")}
                  name="displayName"
                  value={form.displayName}
                  onChange={handleChangeWithLimit}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.EnDisplayName")}
                  name="enDisplayName"
                  value={form.enDisplayName}
                  onChange={handleChangeWithLimit}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.price")}
                  name="price"
                  type="number"
                  value={form.price}
                  onChange={handleChange}
                  required
                />
              </Grid>

              {/* 選填 */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.specs")}
                  name="specs"
                  value={form.specs}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.venderCode")}
                  name="venderCode"
                  value={form.venderCode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.venderName")}
                  name="venderName"
                  value={form.venderName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.brand")}
                  name="brand"
                  value={form.brand}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.movieOrPortrait")}
                  name="movieOrPortrait"
                  value={form.movieOrPortrait}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.nature")}
                  name="nature"
                  value={form.nature}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.productSeries")}
                  name="productSeries"
                  value={form.productSeries}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.categoryName")}
                  name="categoryName"
                  value={form.categoryName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.description")}
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.pVideos")}
                  name="pVideos"
                  value={form.pVideos}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.costPrice")}
                  name="costPrice"
                  value={form.costPrice}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  {/* <InputLabel id="isEnable-label">是否啟用</InputLabel> */}
                  <Select
                    labelId="isEnable-label"
                    id="isEnable"
                    name="isEnable"
                    value={form.isEnable}
                    onChange={handleChange}
                    sx={{ minHeight: 42 }}
                  >
                    <MenuItem value="1">{t("productForm.options.enabled")}</MenuItem>
                    <MenuItem value="0">{t("productForm.options.disabled")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.packagingSize")}
                  name="packagingSize"
                  value={form.packagingSize}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.capsuleSize")}
                  name="capsuleSize"
                  value={form.capsuleSize}
                  onChange={handleChange}
                />
              </Grid>
              {/* <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label={t("productForm.fields.validUntil")}
                  name="validUntil"
                  value={form.validUntil}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid> */}
              {/* <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>是否商城上架</InputLabel>
                  <Select
                    name="mallListing"
                    value={form.mallListing}
                    onChange={handleChange}
                    sx={{ minHeight: 42 }}
                  >
                    <MenuItem value="N">{t("productForm.options.mallListingNo")}</MenuItem>
                    <MenuItem value="Y">{t("productForm.options.mallListingYes")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid> */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.prMethod")}
                  name="prMethod"
                  value={form.prMethod}
                  onChange={handleChange}
                />
              </Grid>
              {/* <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.wAttributes")}
                  name="wAttributes"
                  value={form.wAttributes}
                  onChange={handleChange}
                />
              </Grid> */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.pBrand")}
                  name="pBrand"
                  value={form.pBrand}
                  onChange={handleChange}
                />
              </Grid>
              {/* <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.pAttributes")}
                  name="pAttributes"
                  value={form.pAttributes}
                  onChange={handleChange}
                />
              </Grid> */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.tagList")}
                  name="tagList"
                  value={form.tagList}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t("productForm.fields.notePublic")}
                  name="notePublic"
                  value={form.notePublic}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button
            onClick={handleSubmit}
            sx={{ mt: 2, mb: 2, color: "white !important" }}
            variant="contained"
            disabled={
              (inputMode === "csv" && !csvFile) ||
              (inputMode === "manual" &&
                (!form.code?.trim() ||
                  !form.barcode?.trim() ||
                  !form.name?.trim() ||
                  !form.price ||
                  !form.displayName?.trim()))
            }
          >
            送出
          </Button>
        </DialogActions>
      </Dialog>
      <Slide appear={false} direction="up" in={!trigger}>
        <MDBox
          display={isMobile ? "flex" : "none"}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          position="fixed"
          bottom="0"
          left="0"
          sx={{
            background: "#fff",
            width: "100%",
            padding: "1rem",
          }}
        >
          <Button
            variant="contained"
            onClick={handleOpenDialog}
            sx={{
              backgroundColor: "#FF9800",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#FB8C00" },
              width: "100%",
            }}
          >
            {t("actions.createProduct")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={downloadExcel}
            sx={{
              backgroundColor: "#FF9800",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#FB8C00" },
              width: "100%",
            }}
          >
            {t("actions.exportProductSheet")}
          </Button>
        </MDBox>
      </Slide>
      <Snackbar
        open={Boolean(statusSuccess)}
        autoHideDuration={3000}
        onClose={() => setStatusSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setStatusSuccess("")}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {statusSuccess}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(statusError)}
        autoHideDuration={4000}
        onClose={() => setStatusError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setStatusError("")}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {statusError}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ProductsBK;
